import { API_PREFIX } from '../contracts/routes.js';
import {
  filenameFromDisposition,
  type RawMediaResponse,
  type RawRequest,
  type RawResponse,
  type Transport,
} from './transport.js';

/**
 * The real network transport. Attaches the bearer token, prefixes `/v1`, and never
 * throws for an HTTP error — it returns the parsed envelope and lets the client map it
 * to a typed `ApiError`. A network failure (no response at all) surfaces as a
 * synthetic 0-status body the client renders as an offline error.
 */
export class HttpTransport implements Transport {
  constructor(private readonly baseUrl: string) {}

  async send(req: RawRequest): Promise<RawResponse> {
    const url = new URL(`${this.baseUrl}${API_PREFIX}${req.path}`);
    if (req.query) {
      for (const [key, value] of Object.entries(req.query)) {
        if (value !== undefined) url.searchParams.set(key, value);
      }
    }

    const headers: Record<string, string> = { accept: 'application/json' };
    if (req.token !== null) headers.authorization = `Bearer ${req.token}`;

    const init: RequestInit = { method: req.method, headers };
    if (req.form !== undefined) {
      init.body = req.form; // browser sets the multipart boundary
    } else if (req.body !== undefined) {
      headers['content-type'] = 'application/json';
      init.body = JSON.stringify(req.body);
    }

    try {
      const res = await fetch(url.toString(), init);
      const text = await res.text();
      const json: unknown = text.length > 0 ? JSON.parse(text) : null;
      return { status: res.status, ok: res.ok, json };
    } catch (cause) {
      return {
        status: 0,
        ok: false,
        json: {
          error: {
            code: 'INTERNAL',
            message: 'Could not reach the admin API. Check your connection.',
            requestId: 'offline',
            detail: String(cause),
          },
        },
      };
    }
  }

  /**
   * The binary read path. A 2xx response is read as a `Blob` (never `JSON.parse`d), so an
   * image or PDF attachment comes back as bytes the UI turns into an object URL. A non-2xx
   * response is read as the JSON `{ error }` envelope, exactly like `send`, so the client's
   * error mapping is unchanged.
   */
  async sendMedia(req: RawRequest): Promise<RawMediaResponse> {
    const url = new URL(`${this.baseUrl}${API_PREFIX}${req.path}`);
    if (req.query) {
      for (const [key, value] of Object.entries(req.query)) {
        if (value !== undefined) url.searchParams.set(key, value);
      }
    }
    const headers: Record<string, string> = {};
    if (req.token !== null) headers.authorization = `Bearer ${req.token}`;

    try {
      const res = await fetch(url.toString(), { method: req.method, headers });
      if (res.ok) {
        const blob = await res.blob();
        const name = filenameFromDisposition(res.headers.get('content-disposition'));
        return { status: res.status, ok: true, blob, name, json: null };
      }
      const text = await res.text();
      const json: unknown = text.length > 0 ? JSON.parse(text) : null;
      return { status: res.status, ok: false, blob: null, name: null, json };
    } catch (cause) {
      return {
        status: 0,
        ok: false,
        blob: null,
        name: null,
        json: {
          error: {
            code: 'INTERNAL',
            message: 'Could not load that attachment. Check your connection.',
            requestId: 'offline',
            detail: String(cause),
          },
        },
      };
    }
  }
}
